//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import { PojoMap } from 'pojo-maps';
import { PojoSet } from 'pojo-sets';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import _ from 'underscore';
import { Button, Popover, PopoverContent, PopoverTarget } from 'unison';

import { IConfiguration } from '@apis/tagged-by-curalate-automation/automationApiModels';
import { PrimaryButton } from '@components/atoms/button/Button';
import Icon from '@components/atoms/icon/Icon';
import TextInput from '@components/atoms/text-input/TextInput';
import GalleriesCheckboxList from '@galleries/components/content/galleries-checkbox-list/GalleriesCheckboxList';
import { InstaShopDisabledMessage } from '@galleries/constants/galleriesConstants';
import GalleryContainerId from '@galleries/models/GalleryContainerId';
import { ExperienceContainerType } from '@models/ExperienceContainerType';
import ReelId from '@models/ReelId';
import { actions as streamDrawerActions } from '@streams/actions/streamDrawerActions';
import { AUTO_FILTERS } from '@streams/constants/streamCreationConstants';
import { initialType } from '@streams/models/autoApprovalFieldModal';
import { IStream } from '@streams-drawer/model/IStream';
import {
  StreamsDrawerStreamType,
  nameToStreamType,
} from '@streams-drawer/streams-drawer-constants';

interface Props {
  showOnlyActiveField: boolean;
}

const AutoApprovalFields = ({ showOnlyActiveField }: Props) => {
  const dispatch = useDispatch();
  const recommendationsStreams = useSelector(
    (state) => state.streams.streamsDrawer.recommendationsStreams,
  ) as IStream[];

  const allGalleries = useSelector((state) => state.galleries.data.galleries);

  const taggedByCuralateStream = recommendationsStreams.find(
    (recStream) =>
      nameToStreamType(recStream) === StreamsDrawerStreamType.TAGGED_BY_CURALTE,
  );
  let creatorClient: string[] = [];

  if (taggedByCuralateStream) {
    creatorClient = taggedByCuralateStream.sources.shareCreatorClients ?? [];
  }
  const clientId = useSelector((state) => state.session.client.id);
  //initial input to display when response returns 0
  const initialValue: initialType[] = [
    {
      id: 1,
      configurationName: 'Untitled',
      galleryIds: {},
      isBVNetwork: false,
      isActive: false,
      is_edit_clicked: false,
      is_dropdown_open: false,
      is_dropdown_selection_empty: true,
      length: 0,
    },
  ];
  const taggedByBvAutomationConfiguration: IConfiguration[] = useSelector(
    (state) => state.streams.streamsDrawer.taggedByBvAutomationConfiguration,
  ) as IConfiguration[];

  const galleryselectors = useSelector(
    (state) => state.streams.streamsDrawer.gallerySelection,
  );

  //useState hooks
  const [configuration, setConfiguration] = useState(initialValue);
  const [activeid, setActiveId] = useState(0);
  const [initialCheckboxState, isSettingInitialCheckboxState] = useState(true);
  const [showNoSelectedGalleriesWarning, isShowNoSelectedGalleriesWarning] =
    useState(true);
  const [currentClickedGalleriesId, setcurrentClickedGalleriesId] = useState(0);
  const [addbuttonEnable, setAddbuttonEnable] = useState(false);
  const [activeConsent, showActiveConsent] = useState({
    enable: false,
    message:
      'Activating this will move the pending content to the selected galleries at the scheduled interval.',
    add: 'ADD',
  });
  const [isdropdownOpen, setIsDropdownOpen] = useState(false);
  const [isbvNetwork, setisBVNetwork] = useState(false);
  interface galleryObjType {
    reelid?: boolean;
  }

  //useEffect hook
  useEffect(() => {
    isSettingInitialCheckboxState(false);

    const tbConfigArray: initialType[] = [];

    let galleryObj: galleryObjType = {};
    let editClicked = false,
      dropdownOpen = false,
      editbvNetwork = isbvNetwork,
      editName = '';
    //api call to get the configs on load and set it only if the returned value is > 0
    taggedByBvAutomationConfiguration.length > 0 && setAddbuttonEnable(true),
      taggedByBvAutomationConfiguration.map((tbconfig) => {
        galleryObj = {};
        tbconfig.galleryIds.forEach((element) => {
          element.split(',').forEach((ele) => {
            if (ele !== '') {
              galleryObj[ele as keyof galleryObjType] = true;
            }
          });
        });
        if (configuration.length == 1) {
          configuration
            .filter((value) => {
              return tbconfig.id === value.id;
            })
            .map((editArray) => {
              editClicked = editArray.is_edit_clicked;
              dropdownOpen = editArray.is_dropdown_open;
              editName = editArray.configurationName;
              editbvNetwork = editArray.isBVNetwork;
            });
        }
        tbConfigArray.push({
          id: tbconfig.id,
          configurationName: editClicked
            ? editName
            : tbconfig.configurationName,
          galleryIds: editClicked
            ? galleryselectors
            : tbconfig.id === currentClickedGalleriesId && !tbconfig.isActive
              ? galleryselectors
              : galleryObj,
          isBVNetwork: editClicked
            ? editbvNetwork
            : tbconfig.id === currentClickedGalleriesId && !tbconfig.isActive
              ? editbvNetwork
              : tbconfig.isBVNetwork,
          isActive: editClicked ? false : tbconfig.isActive,
          is_edit_clicked: editClicked,
          is_dropdown_open: isdropdownOpen ? false : dropdownOpen,
          is_dropdown_selection_empty: true,
          length: editClicked
            ? PojoSet.toArray(galleryselectors).length
            : tbconfig.id === currentClickedGalleriesId && !tbconfig.isActive
              ? PojoSet.toArray(galleryselectors).length
              : Object.keys(galleryObj).length,
        });
      });
    configuration.length == 1 && taggedByBvAutomationConfiguration.length > 0
      ? setConfiguration(tbConfigArray)
      : setConfiguration(
          configuration.map((config) =>
            config.id === currentClickedGalleriesId && !config.isActive
              ? {
                  ...config,
                  galleryIds: galleryselectors,
                  length: PojoSet.toArray(galleryselectors).length,
                  is_dropdown_open: isdropdownOpen
                    ? false
                    : config.is_dropdown_open,
                }
              : {
                  ...config,
                  is_dropdown_open: isdropdownOpen
                    ? false
                    : config.is_dropdown_open,
                },
          ),
        );
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [
    taggedByBvAutomationConfiguration,
    galleryselectors,
    isdropdownOpen,
    isbvNetwork,
  ]);

  const handleClickOutside = (e: MouseEvent) => {
    const targetRef = e.target as HTMLInputElement;
    if (!targetRef.closest('div.bazaarvoice_referenceObject')) {
      setIsDropdownOpen(true);
    } else {
      setIsDropdownOpen(false);
    }
  };

  //galleryCheckboxList loading checks
  const isFetchingGalleries = useSelector(
    (state) => state.galleries.data.flags.isFetchingGalleriesList,
  );
  const isLoading = isFetchingGalleries || initialCheckboxState;

  const changeName = (id: number, new_name: string) => {
    setConfiguration(
      configuration.map((config) =>
        config.id === id
          ? { ...config, configurationName: new_name }
          : { ...config },
      ),
    );
  };

  const openDropdown = (id: number, isActive: boolean) => {
    setcurrentClickedGalleriesId(id);
    configuration.map((config) => {
      if (config.id === id) {
        for (const [key] of Object.entries(galleryselectors)) {
          if (key !== '') {
            dispatch(streamDrawerActions.deselectGallery(key as ReelId));
          }
        }
        for (const [key] of Object.entries(config.galleryIds)) {
          if (key !== '') {
            dispatch(streamDrawerActions.selectGallery(key as ReelId));
          }
        }
      }
    });
    if (isActive) {
      setConfiguration(
        configuration.map((config) =>
          config.id === id
            ? { ...config, is_dropdown_open: false }
            : { ...config },
        ),
      );
    } else {
      setConfiguration(
        configuration.map((config) =>
          config.id === id
            ? { ...config, is_dropdown_open: !config.is_dropdown_open }
            : { ...config, is_dropdown_open: false },
        ),
      );
    }
  };

  const instashopDisabled = () => {
    return {
      [ExperienceContainerType.INSTASHOP]: InstaShopDisabledMessage,
    };
  };

  const gallerySelect = (
    galleryContainerId: GalleryContainerId,
    configurationId?: number,
  ) => {
    const poGalleries = PojoMap.values(allGalleries.byId);
    const reelIdGallery = poGalleries.find((poGall) => {
      if (poGall.containerId === galleryContainerId) {
        return poGall.reelId;
      }
    });
    const reelId = reelIdGallery?.reelId;

    configuration.map((config) => {
      if (config.id === configurationId && reelId) {
        if (PojoSet.has(config.galleryIds, reelId)) {
          dispatch(streamDrawerActions.deselectGallery(reelId));
        } else {
          dispatch(streamDrawerActions.selectGallery(reelId));
          isShowNoSelectedGalleriesWarning(true);
        }
      }
    });
  };

  const bvSyndicationToggled = (configurationId?: number) => {
    setisBVNetwork(true);
    setConfiguration(
      configuration.map((config) =>
        config.id === configurationId
          ? { ...config, isBVNetwork: !config.isBVNetwork }
          : { ...config },
      ),
    );
  };
  //add button functionality which adds a new row when clicked
  const addConfiguration = () => {
    setConfiguration([
      ...configuration,
      {
        id: configuration.length + 1,
        configurationName: 'Untitled',
        galleryIds: {},
        isBVNetwork: false,
        isActive: false,
        is_edit_clicked: false,
        is_dropdown_open: false,
        is_dropdown_selection_empty: true,
        length: 0,
      },
    ]);
    setAddbuttonEnable(true);
  };

  //delete button functionality
  const removeConfiguration = (id: number) => {
    // send the id of the row we want to delete using the call - delete.
    const deleteID = id;
    if (taggedByBvAutomationConfiguration.length >= id) {
      const deleteConf = taggedByBvAutomationConfiguration.map(
        (tbConf, index) => {
          const indexNo = index + 1;
          if (indexNo == id) {
            return tbConf;
          }
        },
      ) as IConfiguration[];

      deleteConf.map((deleteid) => {
        if (deleteid !== undefined) {
          id = deleteid.id;
        }
      });
    }
    dispatch(streamDrawerActions.deleteTaggedByBVAutomationConfig(id))
      .then(() => {
        setConfiguration(
          configuration.filter((config) => config.id !== deleteID),
        );
      })
      .catch((e) => {
        throw e;
      });
    //when there are 0 automations

    return;
  };

  //edit button functionality
  const editConfiguration = async (id: number) => {
    // make the configuration deactive
    // change the button to show save and activate
    const activeConfig = await dispatch(
      streamDrawerActions.fetchTaggedByBVAutomationActiveConfigs(),
    );
    let confactiveId = id;
    activeConfig?.map((conf) => {
      confactiveId = conf.id;
    });
    setConfiguration(
      configuration.map((config) =>
        config.id === id
          ? {
              ...config,
              isActive: false,
              is_edit_clicked: true,
              id: confactiveId,
            }
          : { ...config },
      ),
    );
    const configurationObj = configuration.filter((config) => {
      if (config.id == id) {
        config.id = confactiveId;
        config.isActive = false;
        return config;
      }
    });
    formPostData(configurationObj, 'UPDATE');
  };

  //Forming object to send data to API
  const formPostData = (data: initialType[], api: string, id?: number) => {
    const updateData = [] as IConfiguration[];
    // const galleryArray = data ? Object.keys(data.galleryIds) : [];
    let activateData = {} as IConfiguration;
    let configurationObj = {} as initialType;

    switch (api) {
      case 'SAVE': {
        const config = data.find((addData) => {
          return addData.id === id;
        });
        if (config) {
          configurationObj = config;
          activateData = {
            id: configurationObj.id,
            configurationName: configurationObj.configurationName,
            galleryIds: Object.keys(configurationObj.galleryIds),
            isActive: true,
            clientId: parseInt(clientId),
            creatorClientId: creatorClient,
            isDeleted: false,
            isBVNetwork: configurationObj.isBVNetwork,
          };

          dispatch(
            streamDrawerActions.saveTaggedByBVAutomationConfig(activateData),
          ).catch((e) => {
            throw e;
          });
        }
        break;
      }
      case 'UPDATE':
        data.forEach((dataArr) => {
          updateData.push({
            id: dataArr.id,
            configurationName: dataArr.configurationName,
            galleryIds: Object.keys(dataArr.galleryIds),
            isActive: dataArr.isActive,
            clientId: parseInt(clientId),
            creatorClientId: creatorClient,
            isDeleted: false,
            isBVNetwork: dataArr.isBVNetwork,
          });
        });

        dispatch(
          streamDrawerActions.updateTaggedByBVAutomationConfig(updateData),
        ).catch((e) => {
          throw e;
        });
        break;
    }

    setAddbuttonEnable(true);
  };
  //activate deactivate toggle
  const activateDeactivateSaveToggle = async (
    id: number,
    is_edit_clicked: boolean,
    isActive: boolean,
    galleryLength: number,
    event: React.MouseEvent<HTMLElement>,
  ) => {
    const textContent = event.target as HTMLElement;
    const text = textContent.textContent;
    const configurationObj = configuration.find((config) => {
      return config.id == id;
    });
    if (
      (galleryLength == 0 && !configurationObj?.isBVNetwork) ||
      _.isEmpty(configurationObj?.configurationName)
    ) {
      setActiveId(id);
      isShowNoSelectedGalleriesWarning(false);
    } else {
      {
        if (!isActive && !is_edit_clicked) {
          isShowNoSelectedGalleriesWarning(true);
          const activeConfig = await dispatch(
            streamDrawerActions.fetchTaggedByBVAutomationActiveConfigs(),
          );

          _.isEmpty(activeConfig)
            ? showActiveConsent({
                enable: true,
                message:
                  'Activating this will move the pending content to the selected galleries at the scheduled time.',
                add: 'ADD',
              })
            : showActiveConsent({
                enable: true,
                message:
                  'Activating this will deactivate the current auto-distribution and move content to the newly selected galleries at the scheduled time.',
                add: 'ADD',
              });
        }
      }

      // deactivate any active automation
      {
        !isActive && !is_edit_clicked && setActiveId(id);
      }

      // when deactivate is hit send deactivate using the call - update
      // change the button to activate
      // Allow user to edit, hide edit icon
      {
        if (isActive && text === 'Deactivate') {
          const activeConfig = await dispatch(
            streamDrawerActions.fetchTaggedByBVAutomationActiveConfigs(),
          );
          console.log('activeConfig,', activeConfig);
          let confactiveId: number,
            configurationObj: initialType[] = [];
          activeConfig?.map((conf) => {
            confactiveId = conf.id;
          });
          setConfiguration(
            configuration.map((config) =>
              config.id === id
                ? { ...config, id: confactiveId, isActive: false }
                : { ...config },
            ),
          );
          configurationObj = configuration.filter((config) => {
            if (config.id == id) {
              config.id = confactiveId;
              config.isActive = false;
              return config;
            }
          });
          formPostData(configurationObj, 'UPDATE');
        }
      }
      //Save and Activate
      {
        !isActive &&
          is_edit_clicked &&
          setConfiguration(
            configuration.map((config) =>
              config.id === id
                ? {
                    ...config,
                    isActive: false,
                    is_edit_clicked: false,
                  }
                : { ...config },
            ),
          );
        const configurationObj = configuration.filter((config) => {
          if (config.id == id) {
            config.isActive = false;
            return config;
          }
        });
        !isActive &&
          is_edit_clicked &&
          formPostData(configurationObj, 'UPDATE', id);
      }
    }
  };

  // Activate confirmation
  const activateAutomation = async (id: number) => {
    // when activate is hit send activate using the call - add
    // change the button to deactivate
    // dont allow user to edit
    // show edit icon
    {
      setConfiguration(
        configuration.map((config) =>
          config.id === id
            ? {
                ...config,
                galleryIds: config.galleryIds,
                created: 0,
                lastUpdated: 0,
                isDeleted: false,
                clientId: clientId,
                isActive: true,
                is_edit_clicked: false,
              }
            : { ...config, isActive: false },
        ),
      );
      const configurationObj = configuration.filter((config) => {
        if (config.id == id) {
          config.isActive = true;
          return config;
        }
      });
      //Activate and Deactivate
      const autoconfiguration = taggedByBvAutomationConfiguration.filter(
        (autoconf) => {
          return autoconf.id == id;
        },
      );
      if (autoconfiguration.length > 0) {
        const currentActiveConf = configuration.filter((active) => {
          if (active.isActive && active.id !== id) {
            active.isActive = false;
            return active;
          }
        });
        const activeDeactiveConf = [...configurationObj, ...currentActiveConf];
        formPostData(activeDeactiveConf, 'UPDATE', id);
      } else {
        const activeConfig: IConfiguration[] =
          (await dispatch(
            streamDrawerActions.fetchTaggedByBVAutomationActiveConfigs(),
          )) ?? [];
        activeConfig.map((activeCon) => {
          activeCon.isActive = false;
          activeCon.isDeleted = false;
        });
        dispatch(
          streamDrawerActions.updateTaggedByBVAutomationConfig(activeConfig),
        )
          .then(() => {
            formPostData(configurationObj, 'SAVE', id);
          })
          .catch((e) => {
            throw e;
          });
      }
      setAddbuttonEnable(true);
      showActiveConsent({ enable: false, message: '', add: 'ADD' });
    }
  };
  const closeOverlay = () => {
    showActiveConsent({ enable: false, message: '', add: 'ADD' });
  };
  const removeConsent = (id: number, isActive: boolean) => {
    if (isActive) {
      setActiveId(id);
      showActiveConsent({
        enable: true,
        message:
          "Deleting will permanently remove the auto-distribution and it won't move the content back to the pending list.",
        add: 'DELETE',
      });
    } else {
      removeConfiguration(id);
    }
  };

  return (
    <div>
      {configuration.map(
        ({
          configurationName,
          galleryIds,
          id,
          is_dropdown_open,
          is_dropdown_selection_empty,
          is_edit_clicked,
          isActive,
          isBVNetwork,
          length,
        }) => {
          const noGallerySelected =
            PojoSet.toArray(galleryIds).length == 0 && !isBVNetwork;
          const galleryLength = length;
          if (isActive || !showOnlyActiveField) {
            return (
              <div key={id} style={{ position: 'relative' }}>
                <AutoApprovalFieldWrapper data-id={id}>
                  <StyledTextInput
                    onChange={(value) => changeName(id, value)}
                    placeholder={'Enter Name'}
                    readOnly={isActive}
                    value={configurationName}
                  />
                  <StyledPopoverWrapper className='bazaarvoice_referenceObject'>
                    <Popover
                      isOpen={is_dropdown_open}
                      positioningStrategy={{
                        fixed: ['bottom'],
                        align: 'left',
                      }}
                      toggle={() => undefined}
                    >
                      <PopoverTarget allowCustomOnClick>
                        <PopoverButton
                          className='cu-button-default'
                          onClick={() => openDropdown(id, isActive)}
                        >
                          {galleryLength > 0 && isBVNetwork
                            ? `${galleryLength} Gallery, BV Selected`
                            : galleryLength > 0
                              ? `${galleryLength} Gallery Selected`
                              : isBVNetwork
                                ? 'BV Network Selected'
                                : 'Select Galleries'}
                          <Icon name={'chevron-down'} />
                        </PopoverButton>
                      </PopoverTarget>
                      <DropdownPopoverContent>
                        <GalleriesCheckboxList
                          areNoCheckboxesSelected={noGallerySelected}
                          areNoGalleriesSelected={noGallerySelected}
                          configurationId={id}
                          disabledGalleryTypes={instashopDisabled()}
                          gallerySelected={gallerySelect}
                          gallerySelectionState={galleryIds}
                          isBazaarvoiceSyndicationSelected={isBVNetwork}
                          isLoading={isLoading}
                          shouldDisableBazaarvoiceSyndicationCheckbox={false}
                          showHeaderText={is_dropdown_selection_empty}
                          toggleBazaarvoiceSyndication={bvSyndicationToggled}
                        />
                      </DropdownPopoverContent>
                    </Popover>
                  </StyledPopoverWrapper>
                  <ButtonWrapper>
                    <StyledButton
                      isDisabled={false}
                      onClick={(event) =>
                        activateDeactivateSaveToggle(
                          id,
                          is_edit_clicked,
                          isActive,
                          galleryLength,
                          event,
                        )
                      }
                    >
                      {is_edit_clicked && !isActive
                        ? 'Save'
                        : isActive
                          ? 'Deactivate'
                          : 'Activate'}
                    </StyledButton>
                    <DeleteEditButton
                      onClick={() => removeConsent(id, isActive)}
                    >
                      <EditDeleteIcon name='delete' />
                      {/* <img height={18} src={binIcon} alt='delete image' /> */}
                    </DeleteEditButton>

                    {isActive && (
                      <DeleteEditButton onClick={() => editConfiguration(id)}>
                        <EditDeleteIcon name='edit' />
                      </DeleteEditButton>
                    )}
                  </ButtonWrapper>
                </AutoApprovalFieldWrapper>
                {!showNoSelectedGalleriesWarning && activeid === id && (
                  <WarningContainer>
                    <div data-active={activeid} data-id1={id}>
                      {_.isEmpty(configurationName)
                        ? 'Please Enter name'
                        : 'You must select a Gallery before continuing.'}
                    </div>
                  </WarningContainer>
                )}
                {activeConsent.enable && activeid === id && (
                  <ActivationConsent className='activate-confirm'>
                    <ActiveConsentMessage>
                      {activeConsent.message}
                    </ActiveConsentMessage>
                    <ActiveButton>
                      {activeConsent.add == 'ADD' ? (
                        <Button onClick={() => activateAutomation(id)}>
                          <CircleIcon name='circle-checkmark' />
                        </Button>
                      ) : (
                        <Button onClick={() => removeConfiguration(id)}>
                          <CircleIcon name='circle-checkmark' />
                        </Button>
                      )}
                      <Button onClick={() => closeOverlay()}>
                        <CloseIcon name='close' />
                      </Button>
                    </ActiveButton>
                  </ActivationConsent>
                )}
              </div>
            );
          }
        },
      )}
      {!showOnlyActiveField}
      {!showOnlyActiveField}

      {!showOnlyActiveField && (
        <Addbutton>
          <PrimaryButton
            isDisabled={
              configuration.length == 1 && configuration[0]?.isActive
                ? false
                : configuration.length > AUTO_FILTERS.configLength - 1 ||
                  !addbuttonEnable
            }
            onClick={() => addConfiguration()}
          >
            <Icon name='new' /> Add
          </PrimaryButton>
        </Addbutton>
      )}
      {!showOnlyActiveField}
      {!showOnlyActiveField && (
        <AddLimitText>
          Add
          <BoldText>
            {AUTO_FILTERS.configLength - configuration.length}
          </BoldText>
          more distribution list
        </AddLimitText>
      )}
    </div>
  );
};

const AutoApprovalFieldWrapper = styled.div(
  ({ theme }) => `
  background-color: ${theme.colors.gray0};
  border: 0.5px solid ${theme.colors.gray2};
  padding: ${theme.spacings[2]};
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`,
);
const DropdownPopoverContent = styled(PopoverContent)`
  height: 200px;
  padding: ${({ theme }) => theme.spacings[3]};
  overflow: scroll;
  background-color: ${({ theme }) => theme.colors.white};
  max-width: 332px;
`;

const StyledPopoverWrapper = styled.div`
  width: 300px;
  text-align: center;
`;

const StyledTextInput = styled(TextInput)`
  width: 124px;
`;
const ButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-start;
  width: 35%;
  align-items: center;
`;
const PopoverButton = styled(Button)`
  width: 200px;
  text-align: left !important;
  border-radius: 2px !important;
  && {
    color: ${({ theme }) => theme.colors.gray9}!important;
  }
  svg {
    float: right;
  }
`;

const StyledButton = styled(PrimaryButton)`
  border: 0px solid;
  font-weight: normal !important;
  min-width: 125px;
`;
const WarningContainer = styled.div`
  padding: ${({ theme }) => theme.spacings[2]};
  width: 100%;
  position: relative;
  left: 0px;
  z-index: 9;
  && {
    color: ${({ theme }) => theme.colors.red8};
  }
  display: flex;
  flex-direction: column;
`;
const ActivationConsent = styled.div`
  width: 100%;
  background-color: #2f2f2f;
  && {
    padding: ${({ theme }) => theme.spacings[2]}
      ${({ theme }) => theme.spacings[3]};
    color: ${({ theme }) => theme.colors.white};
  }
  position: absolute;
  top: 4px;
  opacity: 0.95;
  z-index: 99;
  display: flex;
  justify-content: space-between;
  align-items: self-end;
`;
const ActiveConsentMessage = styled.div`
  width: 80%;
  line-height: ${({ theme }) => theme.lineHeights.uiLeading};
`;
const ActiveButton = styled.div(
  ({ theme }) => `
button {
  padding-left: ${theme.spacings[3]} !important;
}
`,
);
const DeleteEditButton = styled.button(
  ({ theme }) => `
  padding-left: ${theme.spacings[3]};
  background-color: transparent;
  border: none;
`,
);
const AddLimitText = styled.div`
  font-size: ${({ theme }) => theme.fontSizes[6]};
`;
const Addbutton = styled.div(
  ({ theme }) => `
margin: ${theme.spacings[5]} 0 ${theme.spacings[2]};
svg {
  width: 12px;
  height: 12px;
  vertical-align: -2px;
}
`,
);
const BoldText = styled.span(
  ({ theme }) => `
  font-weight: ${theme.fontWeights.bold};
  padding: ${theme.spacings[1]};
`,
);

const CircleIcon = styled(Icon)`
  color: ${({ theme }) => theme.colors.aliases.success};
`;
const CloseIcon = styled(Icon)`
  color: ${({ theme }) => theme.colors.yellow5};
`;
const EditDeleteIcon = styled(Icon)(
  ({ theme }) => `
  color:${theme.colors.gray7};
  vertical-align: middle;
  height: ${theme.spacings[3]};
  width: ${theme.spacings[3]};
`,
);

export default AutoApprovalFields;
